import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  Chip,
  Card,
  CardContent,
  Grid,
  Slider,
  LinearProgress,
  Fade,
  Zoom,
  Slide,
  Grow
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  Close,
  VolumeUp,
  VolumeOff,
  Speed,
  Replay
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const DemoAha = ({ open, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);

  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const demoSteps = [
    {
      title: "The Pain Point",
      duration: 60000,
      component: PainPointScene,
      audio: "/audio/demo-scene1.mp3"
    },
    {
      title: "Dashboard Revelation", 
      duration: 60000,
      component: DashboardRevelation,
      audio: "/audio/demo-scene2.mp3"
    },
    {
      title: "Capability Assessment",
      duration: 120000,
      component: CapabilityAssessment,
      audio: "/audio/demo-scene3.mp3"
    },
    {
      title: "Bubble Chart Insight",
      duration: 120000,
      component: BubbleChartScene,
      audio: "/audio/demo-scene4.mp3"
    },
    {
      title: "Scenario Comparison",
      duration: 120000,
      component: ScenarioComparison,
      audio: "/audio/demo-scene5.mp3"
    },
    {
      title: "Executive Reporting",
      duration: 60000,
      component: ExecutiveReporting,
      audio: "/audio/demo-scene6.mp3"
    },
    {
      title: "Implementation Roadmap",
      duration: 60000,
      component: ImplementationRoadmap,
      audio: "/audio/demo-scene7.mp3"
    },
    {
      title: "The Transformation",
      duration: 120000,
      component: TransformationScene,
      audio: "/audio/demo-scene8.mp3"
    }
  ];

  // Auto-play when demo opens
  useEffect(() => {
    if (open && !userInteracted) {
      const timer = setTimeout(() => {
        setIsPlaying(true);
        setUserInteracted(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [open, userInteracted]);

  // Demo progression logic
  useEffect(() => {
    if (!isPlaying) return;

    const stepDuration = demoSteps[currentStep].duration / speed;
    const increment = 100 / (stepDuration / 100);

    timerRef.current = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + increment;
        if (newProgress >= 100) {
          if (currentStep < demoSteps.length - 1) {
            setCurrentStep(prev => prev + 1);
            return 0;
          } else {
            setIsPlaying(false);
            return 100;
          }
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(timerRef.current);
  }, [isPlaying, currentStep, speed, demoSteps]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    setUserInteracted(true);
  };

  const handleNext = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
      setProgress(0);
    }
    setUserInteracted(true);
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setProgress(0);
    }
    setUserInteracted(true);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setProgress(0);
    setIsPlaying(true);
    setUserInteracted(true);
  };

  const CurrentScene = demoSteps[currentStep].component;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          minHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" fontWeight="bold">
            🎬 The "Aha!" Experience
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => setAudioEnabled(!audioEnabled)} sx={{ color: 'white' }}>
              {audioEnabled ? <VolumeUp /> : <VolumeOff />}
            </IconButton>
            <IconButton onClick={onClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        
        <Stepper activeStep={currentStep} sx={{ mt: 3 }}>
          {demoSteps.map((step, index) => (
            <Step key={index}>
              <StepLabel sx={{ 
                '& .MuiStepLabel-label': { 
                  color: 'white',
                  fontSize: '0.75rem'
                } 
              }}>
                {step.title}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 0, position: 'relative', minHeight: '60vh' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            style={{ height: '100%' }}
          >
            <CurrentScene 
              isPlaying={isPlaying}
              progress={progress}
              currentStep={currentStep}
            />
          </motion.div>
        </AnimatePresence>

        {/* Progress Bar */}
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0,
            height: 4,
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff6b6b, #ffa726)'
            }
          }} 
        />
      </DialogContent>

      {/* Controls */}
      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={handlePrevious} disabled={currentStep === 0} sx={{ color: 'white' }}>
              <SkipPrevious />
            </IconButton>
            
            <IconButton onClick={handlePlayPause} sx={{ color: 'white', fontSize: '2rem' }}>
              {isPlaying ? <Pause fontSize="inherit" /> : <PlayArrow fontSize="inherit" />}
            </IconButton>
            
            <IconButton onClick={handleNext} disabled={currentStep === demoSteps.length - 1} sx={{ color: 'white' }}>
              <SkipNext />
            </IconButton>

            <IconButton onClick={handleRestart} sx={{ color: 'white', ml: 1 }}>
              <Replay />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Speed sx={{ color: 'white' }} />
            <Slider
              value={speed}
              onChange={(e, newValue) => setSpeed(newValue)}
              min={0.5}
              max={2}
              step={0.5}
              sx={{ width: 100, color: 'white' }}
            />
            <Typography variant="body2" sx={{ color: 'white', minWidth: 40 }}>
              {speed}x
            </Typography>
          </Box>

          <Button 
            variant="contained" 
            onClick={onClose}
            sx={{ 
              bgcolor: 'white', 
              color: '#667eea',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            Start Your Own Aha! Moment
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

// Scene Components
const PainPointScene = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
    <Fade in={isPlaying && progress < 25} timeout={1000}>
      <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
        🚨 The Million-Euro Problem
      </Typography>
    </Fade>

    <Fade in={isPlaying && progress >= 25 && progress < 50} timeout={1000}>
      <Box sx={{ textAlign: 'center', my: 4 }}>
        <Typography variant="h5" gutterBottom>
          "We have €20M to invest in digital transformation..."
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          "...but every executive has a different opinion on where to start."
        </Typography>
      </Box>
    </Fade>

    <Fade in={isPlaying && progress >= 50} timeout={1000}>
      <Grid container spacing={3} sx={{ mt: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">CFO Says:</Typography>
              <Typography>"We need cost reduction first!"</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">COO Says:</Typography>
              <Typography>"Operational efficiency is the priority!"</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">CTO Says:</Typography>
              <Typography>"Without innovation, we'll be left behind!"</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Fade>

    <Fade in={isPlaying && progress >= 75} timeout={1000}>
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h4" color="error.light">
          Sound familiar?
        </Typography>
        <Typography variant="h6" sx={{ mt: 2, opacity: 0.9 }}>
          This is where most companies waste millions on the wrong priorities.
        </Typography>
      </Box>
    </Fade>
  </Box>
);

const DashboardRevelation = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%' }}>
    <Fade in={isPlaying} timeout={1000}>
      <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
        💡 The Revelation
      </Typography>
    </Fade>

    <Grid container spacing={4} sx={{ mt: 2 }}>
      <Grid item xs={12} md={6}>
        <Fade in={isPlaying && progress >= 20} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom>
                Within seconds, ROIAnalyzer reveals:
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Zoom in={isPlaying && progress >= 30}>
                  <Chip 
                    label="€14.2M in untapped annual value" 
                    color="success" 
                    sx={{ fontSize: '1.2rem', p: 2, mb: 2 }}
                  />
                </Zoom>
                <Zoom in={isPlaying && progress >= 40}>
                  <Chip 
                    label="€4.8M in quick wins (<6 months)" 
                    color="warning" 
                    sx={{ fontSize: '1.2rem', p: 2, mb: 2 }}
                  />
                </Zoom>
                <Zoom in={isPlaying && progress >= 50}>
                  <Chip 
                    label="47% faster implementation" 
                    color="info" 
                    sx={{ fontSize: '1.2rem', p: 2 }}
                  />
                </Zoom>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>

      <Grid item xs={12} md={6}>
        <Fade in={isPlaying && progress >= 60} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 Quick Wins Identified
              </Typography>
              <Box sx={{ mt: 2 }}>
                {['Digital Workflow Automation', 'Supply Chain Optimization', 'AI-Powered Quality Control'].map((win, index) => (
                  <Slide key={index} in={isPlaying && progress >= (70 + index * 10)} timeout={500}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}>
                      <CheckCircle sx={{ color: 'success.main', mr: 2 }} />
                      <Typography>{win}</Typography>
                      <Chip label="€1.2M-€1.8M" size="small" color="success" sx={{ ml: 'auto' }} />
                    </Box>
                  </Slide>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>
    </Grid>
  </Box>
);

const CapabilityAssessment = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%' }}>
    <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
      🎚️ Capability Assessment Magic
    </Typography>

    <Grid container spacing={4} sx={{ mt: 2 }}>
      <Grid item xs={12} md={6}>
        <Fade in={isPlaying} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Operational Efficiency
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography gutterBottom>Current: Level 3 (Basic Standardization)</Typography>
                <Slider
                  value={[3, isPlaying && progress >= 50 ? 7 : 3]}
                  min={0}
                  max={10}
                  sx={{ mb: 2 }}
                  disabled
                />
                <Zoom in={isPlaying && progress >= 60}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(76,175,80,0.2)', borderRadius: 2 }}>
                    <Typography variant="h5" color="success.main">
                      €2.3M Annual Value Unlocked
                    </Typography>
                  </Box>
                </Zoom>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>

      <Grid item xs={12} md={6}>
        <Fade in={isPlaying && progress >= 70} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Performance Dimensions
              </Typography>
              <Box sx={{ mt: 2 }}>
                {[
                  { label: 'Operational Efficiency', value: 70 },
                  { label: 'Time to Market', value: 85 },
                  { label: 'Innovation Capacity', value: 60 },
                  { label: 'Cost Optimization', value: 75 }
                ].map((dimension, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{dimension.label}</Typography>
                      <Typography variant="body2">{dimension.value}%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={isPlaying && progress >= (80 + index * 5) ? dimension.value : 0}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>
    </Grid>
  </Box>
);

const BubbleChartScene = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%' }}>
    <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
      🔵 The Strategic Bubble Chart
    </Typography>

    <Box sx={{ position: 'relative', height: '400px', mt: 4, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, p: 3 }}>
      {/* Quadrants */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: '50%', bottom: '50%', bgcolor: 'rgba(76,175,80,0.1)', borderRadius: '16px 0 0 0' }} />
      <Box sx={{ position: 'absolute', top: 0, left: '50%', right: 0, bottom: '50%', bgcolor: 'rgba(33,150,243,0.1)', borderRadius: '0 16px 0 0' }} />
      <Box sx={{ position: 'absolute', top: '50%', left: 0, right: '50%', bottom: 0, bgcolor: 'rgba(255,152,0,0.1)', borderRadius: '0 0 0 16px' }} />
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', right: 0, bottom: 0, bgcolor: 'rgba(244,67,54,0.1)', borderRadius: '0 0 16px 0' }} />

      {/* Labels */}
      <Typography sx={{ position: 'absolute', top: 20, left: 20, fontWeight: 'bold' }}>Quick Wins</Typography>
      <Typography sx={{ position: 'absolute', top: 20, right: 20, fontWeight: 'bold' }}>Major Projects</Typography>
      <Typography sx={{ position: 'absolute', bottom: 20, left: 20, fontWeight: 'bold' }}>Fill-ins</Typography>
      <Typography sx={{ position: 'absolute', bottom: 20, right: 20, fontWeight: 'bold' }}>Thankless Tasks</Typography>

      {/* Bubbles */}
      <AnimatePresence>
        {isPlaying && progress >= 30 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            style={{ position: 'absolute', top: '30%', left: '30%' }}
          >
            <Box sx={{ width: 80, height: 80, bgcolor: 'success.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
              €1.8M
            </Box>
          </motion.div>
        )}

        {isPlaying && progress >= 50 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ position: 'absolute', top: '20%', left: '70%' }}
          >
            <Box sx={{ width: 120, height: 120, bgcolor: 'primary.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
              €4.2M
            </Box>
          </motion.div>
        )}

        {isPlaying && progress >= 70 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ position: 'absolute', top: '70%', left: '20%' }}
          >
            <Box sx={{ width: 60, height: 60, bgcolor: 'warning.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
              €0.8M
            </Box>
          </motion.div>
        )}

        {isPlaying && progress >= 90 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            style={{ position: 'absolute', top: '65%', left: '75%' }}
          >
            <Box sx={{ width: 50, height: 50, bgcolor: 'error.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
              €0.3M
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>

    <Fade in={isPlaying && progress >= 80}>
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">
          "In 30 seconds, we've created a strategic roadmap that would take months of analysis."
        </Typography>
      </Box>
    </Fade>
  </Box>
);

const ScenarioComparison = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%' }}>
    <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
      ⚖️ Scenario Comparison
    </Typography>

    <Grid container spacing={4} sx={{ mt: 2 }}>
      <Grid item xs={12} md={6}>
        <Fade in={isPlaying} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary.light">
                Digital First Strategy
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography>💰 Total Value: <strong>€18.2M</strong></Typography>
                <Typography>⏱️ Timeline: <strong>24 months</strong></Typography>
                <Typography>🎯 Focus: <strong>Innovation & Growth</strong></Typography>
                <Chip label="Higher long-term value" color="success" sx={{ mt: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>

      <Grid item xs={12} md={6}>
        <Fade in={isPlaying && progress >= 40} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="warning.light">
                Operational Excellence
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography>💰 Total Value: <strong>€12.4M</strong></Typography>
                <Typography>⏱️ Timeline: <strong>14 months</strong></Typography>
                <Typography>🎯 Focus: <strong>Cost & Efficiency</strong></Typography>
                <Chip label="Faster implementation" color="warning" sx={{ mt: 2 }} />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Grid>

      <Grid item xs={12}>
        <Fade in={isPlaying && progress >= 70} timeout={1000}>
          <Card sx={{ bgcolor: 'rgba(255,193,7,0.2)', backdropFilter: 'blur(10px)', mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🤖 AI Recommendation
              </Typography>
              <Typography>
                "Hybrid approach: Start with Operational Excellence quick wins, then phase in Digital Transformation. 
                This balances short-term cash flow with long-term growth."
              </Typography>
            </CardContent>
          </Card>
        </Fade>
      </Grid>
    </Grid>
  </Box>
);

const ExecutiveReporting = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%', textAlign: 'center' }}>
    <Typography variant="h3" fontWeight="bold" gutterBottom>
      📄 Executive Reporting
    </Typography>

    <Fade in={isPlaying} timeout={1000}>
      <Typography variant="h6" sx={{ opacity: 0.9, mb: 4 }}>
        "Now they need to get board approval. Watch what happens with one click..."
      </Typography>
    </Fade>

    <Zoom in={isPlaying && progress >= 30}>
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <Card sx={{ width: 300, height: 400, bgcolor: 'rgba(255,255,255,0.9)', color: 'black', p: 3 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom color="primary">
              Executive Report
            </Typography>
            <Box sx={{ textAlign: 'left', mt: 3 }}>
              {['Executive Summary', 'Financial Analysis', 'Strategic Recommendations', 'Implementation Plan', 'Risk Assessment'].map((section, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle color="success" sx={{ mr: 1 }} />
                  <Typography>{section}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
        
        <Fade in={isPlaying && progress >= 60}>
          <Chip 
            label="Generated in 3 seconds" 
            color="success" 
            sx={{ position: 'absolute', top: -10, right: -10, fontWeight: 'bold' }}
          />
        </Fade>
      </Box>
    </Zoom>

    <Fade in={isPlaying && progress >= 80}>
      <Typography variant="h6" sx={{ mt: 4, opacity: 0.9 }}>
        "30-page board-ready report. Normally this would take a team weeks."
      </Typography>
    </Fade>
  </Box>
);

const ImplementationRoadmap = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%' }}>
    <Typography variant="h3" fontWeight="bold" textAlign="center" gutterBottom>
      🛣️ Implementation Roadmap
    </Typography>

    <Box sx={{ mt: 4, position: 'relative' }}>
      {[
        { quarter: 'Q1 2024', focus: 'Quick Wins', value: '€4.8M', color: 'success' },
        { quarter: 'Q2-Q3 2024', focus: 'Scale Success', value: '€6.2M', color: 'primary' },
        { quarter: 'Q4 2024-2025', focus: 'Digital Transformation', value: '€7.2M', color: 'warning' }
      ].map((phase, index) => (
        <Fade key={index} in={isPlaying && progress >= (index * 30)} timeout={1000}>
          <Card sx={{ mb: 2, p: 3, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">{phase.quarter}</Typography>
                <Typography sx={{ opacity: 0.8 }}>{phase.focus}</Typography>
              </Box>
              <Chip label={phase.value} color={phase.color} />
            </Box>
          </Card>
        </Fade>
      ))}
    </Box>

    <Fade in={isPlaying && progress >= 90}>
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h6">
          "Every step tied to specific ROI metrics and risk mitigation."
        </Typography>
      </Box>
    </Fade>
  </Box>
);

const TransformationScene = ({ isPlaying, progress }) => (
  <Box sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
    <Fade in={isPlaying} timeout={1000}>
      <Typography variant="h3" fontWeight="bold" gutterBottom>
        🌟 The Transformation
      </Typography>
    </Fade>

    <Fade in={isPlaying && progress >= 30} timeout={1000}>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" color="success.light" gutterBottom>
          From Confusion to Clarity
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          From political battles to data-driven alignment
        </Typography>
      </Box>
    </Fade>

    <Grid container spacing={4} sx={{ mt: 2 }}>
      {[
        { role: 'CFO', quote: '"The numbers are undeniable. We start with the quick wins."', color: 'success' },
        { role: 'COO', quote: '"The roadmap makes perfect sense for operations."', color: 'primary' },
        { role: 'CTO', quote: '"Finally, a plan that balances innovation with execution."', color: 'warning' }
      ].map((exec, index) => (
        <Grid item xs={12} md={4} key={index}>
          <Slide in={isPlaying && progress >= (50 + index * 15)} timeout={500} direction="up">
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Typography variant="h6" color={`${exec.color}.light`} gutterBottom>
                  {exec.role}
                </Typography>
                <Typography sx={{ fontStyle: 'italic', mt: 2 }}>
                  {exec.quote}
                </Typography>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      ))}
    </Grid>

    <Fade in={isPlaying && progress >= 95} timeout={1000}>
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          🎯 Ready for Your Aha! Moment?
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          Stop guessing. Start knowing.
        </Typography>
      </Box>
    </Fade>
  </Box>
);

// Helper component
const CheckCircle = ({ color = 'success', ...props }) => (
  <Box
    component="span"
    sx={{
      color: `${color}.main`,
      fontSize: '1.2rem'
    }}
    {...props}
  >
    ✓
  </Box>
);

export default DemoAha;